import { LitElement, css, html, unsafeCSS } from 'lit';
import styles from './radioOption.scss?inline';
import { EVENTS } from '../constants';
import { InputStyleController } from '@/controllers/InputStyleController';
import { createRef, ref } from 'lit/directives/ref.js';

export default class SotbitRadioOption extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        id: { type: String },
        value: { type: String },
        name: { type: String },
        checked: { type: Boolean, reflect: true },
        disabled: { type: Boolean },
    };

    constructor() {
        super();

        this.id = '';
        this.value = '';
        this._value = '';
        this._defaultValue = '';
        this.name = '';
        this.checked = false;
        this._defaultChecked = false;
        this.disabled = false;
        this.inputRef = createRef();
        this.input = null;

        this._controller = new InputStyleController(this, {
            getInput: () => {
                return this.input;
            },
        });
        this._controller.inputObserverConfig?.attributeFilter?.push('checked');

        this.onInputChange = this.onInputChange.bind(this);
    }

    get value() {
        return this.input ? this.input.value : this._value;
    }

    set value(value) {
        this._value = value;

        if (this.input) {
            this.input.value = value;
        }
    }

    get checked() {
        return this.input ? this.input.checked : this._checked;
    }

    set checked(checked) {
        this._checked = checked;

        if (this.input) {
            this.input.checked = checked;
        }
    }

    get defaultValue() {
        return this._defaultValue;
    }

    get defaultChecked() {
        return this._defaultChecked;
    }

    firstUpdated(_changedProperties) {
        super.firstUpdated(_changedProperties);

        this.value = this.getAttribute('value');
        this._defaultValue = this.value;
        this._defaultChecked = this.checked;
    }

    connectedCallback() {
        super.connectedCallback();

        setTimeout(() => {
            if (!this.input) {
                this.input = this.inputRef?.value;
            }

            if (this.input) {
                this._controller?.inputObserver?.observe(this.input, this._controller.inputObserverConfig);
            }
        });
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._controller?.inputObserver?.disconnect();
    }

    onInputChange(e) {
        this.dispatchEvent(
            new CustomEvent(EVENTS.CHANGE, {
                bubbles: true,
                composed: true,
                detail: {
                    originalEvent: e,
                    value: this.value,
                },
            })
        );
    }

    _handleLabelClick(e) {
        if (!this.input) {
            this.input = this.inputRef?.value;
        }

        if (!this.checked) {
            this.checked = true;
            this.onInputChange(e);
        }
    }

    _handleInputSlotChange(e) {
        this.input?.removeEventListener('change', this.onInputChange);
        this.input = e.currentTarget.assignedElements()[0];

        if (!this.input) {
            this.input = this.inputRef?.value;
        }

        this.input?.removeEventListener('change', this.onInputChange);
        this.input?.addEventListener('change', this.onInputChange);
        this._controller?.inputObserver?.disconnect();
        this._controller?.initHandlers();
        this._controller?.inputObserver.observe(this.input, this._controller?.inputObserverConfig);
        this._controller?.calcInputAttr();
    }

    render() {
        return html`
            <label part="radio-label-wrapper" class="radio-label-wrapper" @click="${this._handleLabelClick}">
                <slot name="input" @slotchange="${this._handleInputSlotChange}">
                    <input
                        ${ref(this.inputRef)}
                        type="radio"
                        id="${this.id}"
                        name="${this.name}"
                        .value="${this.value}"
                    />
                </slot>
                <div part="radio-content" class="radio-content">
                    <span part="radio-custom" class="radio-custom"></span>
                    <span part="radio-text" class="radio-text">
                        <slot></slot>
                    </span>
                </div>
            </label>
        `;
    }
}
